import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const WHALE_LAMPORTS = 500_000_000; // 0.5 SOL

const short = (pk) => (pk ? `${pk.slice(0, 4)}…${pk.slice(-4)}` : "someone");

/** Polls recent tips; when a new one lands while you're on the page,
 * shows a slide-in alert so visitors feel the live action. */
export default function WhaleAlert({ slug }) {
  const [alert, setAlert] = useState(null); // { sol, payer, whale }
  const seen = useRef(null); // newest ts at mount — only newer tips alert
  const timer = useRef(null);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get(`/coins/${slug}/tips/recent?limit=3`);
        if (!alive) return;
        const tips = data.tips || [];
        if (seen.current === null) {
          seen.current = tips[0]?.ts || 0;
          return;
        }
        const fresh = tips.filter((t) => t.ts > seen.current);
        if (fresh.length > 0) {
          seen.current = fresh[0].ts;
          const t = fresh[0];
          setAlert({
            sol: t.amount_lamports / 1_000_000_000,
            payer: short(t.payer_pubkey),
            whale: t.amount_lamports >= WHALE_LAMPORTS,
          });
          clearTimeout(timer.current);
          timer.current = setTimeout(() => setAlert(null), 8000);
        }
      } catch {
        // Poll again next tick.
      }
    };
    poll();
    const i = setInterval(poll, 20000);
    return () => {
      alive = false;
      clearInterval(i);
      clearTimeout(timer.current);
    };
  }, [slug]);

  if (!alert) return null;

  return (
    <div
      className="fixed bottom-6 left-6 z-50 animate-in slide-in-from-left duration-500"
      data-testid="whale-alert"
    >
      <div
        className={`border-2 px-5 py-4 bg-[#121212] shadow-2xl flex items-center gap-3 ${
          alert.whale ? "border-[#00D4FF]" : "border-[#cfff04]"
        }`}
      >
        <span className="text-2xl" aria-hidden>
          {alert.whale ? (
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#00D4FF" strokeWidth="2">
              <path d="M3 12c0-4 3-7 9-7s9 3 9 7c0 3-2 7-9 7s-9-4-9-7z" />
              <circle cx="8" cy="11" r="1" fill="#00D4FF" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cfff04" strokeWidth="2">
              <circle cx="12" cy="12" r="8" />
              <path d="M12 8v8M9 10.5h6" />
            </svg>
          )}
        </span>
        <div>
          <div
            className={`font-mono text-[11px] uppercase tracking-[0.25em] ${
              alert.whale ? "text-[#00D4FF]" : "text-[#cfff04]"
            }`}
            data-testid="whale-alert-label"
          >
            {alert.whale ? "// Whale alert" : "// Tip landed"}
          </div>
          <div className="font-display font-bold text-sm mt-0.5 text-white">
            {alert.payer} just tipped {alert.sol.toFixed(alert.sol >= 1 ? 2 : 3)} SOL
          </div>
        </div>
      </div>
    </div>
  );
}
